import { useMutation, useQuery } from "react-query";

import apiBase from "../../utils/apiBase";

interface FetchAllMentorParams {
  race?: string;
  gender?: string;
  search?: string | null;
  jobTitle?: string;
  industry?: string;
  pageIndex?: number;
  pageSize?: number;
}

export interface Mentor {
  mentorId: string;
  firstName: string;
  lastName: string;
  headline: string;
  company: string;
  department: string;
  industry: string;
  jobTitle: string;
  race: string;
  location: string;
  previewUrl: string;
  isFeatured: boolean;
  gender: string;
  videoLoopUrl: string;
}

export interface FetchAllMentorResponse {
  items: Mentor[];
  filteredCount: number;
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  search: string;
}

interface NegotiationProps {
  mentorId: string;
  offer: RTCSessionDescriptionInit;
  apiKey: string;
}

async function fetchAllMentors({
  gender,
  race,
  search,
  jobTitle,
  industry,
  pageIndex,
  pageSize,
}: FetchAllMentorParams) {
  let path = "/mentors";
  const params: Record<string, unknown> = {};
  if (race) params.Race = race;
  if (gender) params.Gender = gender;
  if (search) {
    params.Search = search;
    path = "/mentors/semantic";
  }
  if (jobTitle) params.JobTitle = jobTitle;
  if (industry) params.Industry = industry;
  if (pageIndex) params.PageIndex = pageIndex;
  if (pageSize) params.PageSize = pageSize;

  return await apiBase.get<
    unknown,
    FetchAllMentorResponse | FetchAllMentorResponse["items"]
  >(path, {
    headers: {
      "X-API-VERSION": "2.0",
    },
    params,
  });
}

export async function doNegotiation({
  mentorId,
  offer,
  apiKey,
}: NegotiationProps) {
  const apiUrl = `${process.env.REACT_APP_PROXY_API_URL}/v1/realtime`;

  // Send offer to the Avatar Realtime API and receive the answer
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      server_type: "hd",
      mentor_id: mentorId,
      offer,
      use_green_video: false,
      use_idle_timeout: false,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to get response from the API");
  }

  const data = await response.json();
  return data;
}

export const useMentors = (req?: FetchAllMentorParams) => {
  return useQuery({
    queryKey: ["fetch-all-mentors", req?.pageIndex, req?.pageSize, req?.search],
    queryFn: () => fetchAllMentors(req ?? {}),
  });
};

async function fetchMentorById(mentorId: string) {
  return await apiBase.get<unknown, Mentor>(`/mentors/${mentorId}`, {
    headers: {
      "X-API-VERSION": "2.0",
    },
  });
}

export const useMentorById = (mentorId: string) => {
  return useQuery({
    queryKey: ["fetch-mentor-by-id", mentorId],
    queryFn: () => fetchMentorById(mentorId),
  });
};

export const useNegotiation = () => {
  return useMutation({
    mutationFn: (query: NegotiationProps) => {
      return doNegotiation(query);
    },
  });
};
